import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { filter, find, sortBy, flow } from 'lodash/fp';
import { omit } from 'lodash';
import { MenuItem, FormControl, InputLabel, Select } from '@material-ui/core';
import CurrencyTextField from '@unicef/material-ui-currency-textfield';
import { useSentryUtils } from 'common-components/SentryUtils/SentryUtilsProvider';

import { browserName, browserVersion, osName, osVersion, isPrivateBrowsing, isDesktop } from 'utils/mobileDetect';
import {
    ALL_INCOME_OR_ASSET_TYPES,
    FINANCIAL_STREAM_ASSET,
    INCOME_TYPE_OTHER,
    ASSET_TYPE_OTHER,
    ERROR_UPLOAD,
    FINANCIAL_STREAM_INCOME,
    FINANCIAL_STREAM_VOUCHER,
    INCOME_OR_ASSET_TYPE_VOUCHER_HOUSING_AUTHORITY,
} from 'constants/constants';
import { allValuesSet } from 'utils/formik';

import ActionButton from 'common-components/ActionButton/ActionButton';
import FormTextInput from 'common-components/FormTextInput/FormTextInput';
import UploadDocuments from './UploadDocuments';
import { Spacer } from 'assets/styles';
import { fileLikeObjectToBase64 } from 'utils/misc';
import { VeryGoodSecurityApi } from 'api/api';
import hooks from '../bankingHooks';

// Types
import { type ConfigurationState, type FinancialDocumentsValidations } from '../pages/PlaidAddPayroll';
import {TextField} from "formik-material-ui";

type FinancialSourceFormFormikValues = {
    income_or_asset_type: number;
    estimated_amount: string;
    uploadedDocuments: {
        [id: string]: {
            id: number;
            label: string; // 'Offer letter'
            files: {
                name: string;
                id: string;
                file: File;
            }[];
        };
    };
    uploaded_documents: string[];
    other: null | string;
};

export interface AddFinancialSourceFormProps {
    initialValues?: FinancialSourceFormFormikValues | undefined;
    plaidIncomeForm?: boolean;
    config?: ConfigurationState;
    onSubmit?: import('formik').FormikConfig<FinancialSourceFormFormikValues>['onSubmit'];
    setError?: (error: string[]) => void;
    financialType: number;
    isEditing?: boolean;
}

export const getTypeLabel = (financialType: number): string | null => {
    if (financialType === FINANCIAL_STREAM_ASSET) {
        return 'Asset';
    }
    if (financialType === FINANCIAL_STREAM_INCOME) {
        return 'Income';
    }
    if (financialType === FINANCIAL_STREAM_VOUCHER) {
        return 'Voucher';
    }
    return null;
}

export function AddFinancialSourceForm(props: AddFinancialSourceFormProps) {
    const bankingContext = hooks.useBankingContext();
    const { logToSentry } = useSentryUtils();
    const isAsset = props.financialType === FINANCIAL_STREAM_ASSET;
    const isIncome = props.financialType === FINANCIAL_STREAM_INCOME;
    const isVoucher = props.financialType === FINANCIAL_STREAM_VOUCHER;

    const financialTypeLabel = getTypeLabel(props.financialType);

    const selectChoices = useMemo(() => {
        const filterObject = {
            stream_type: props.financialType,
            ...(props?.plaidIncomeForm
                ? {
                      allow_manual_document_upload_plaid: true,
                  }
                : {}),
        };
        const filterByType = filter(filterObject);
        const sortByType = sortBy(['income_or_asset_type']);

        return flow(
            filterByType,
            sortByType
        )(props.config?.financial_documents_validations) as unknown[] as FinancialDocumentsValidations[];
    }, [props?.financialType, props.config?.financial_documents_validations]);

    function getInitialValues(possiblySelectedChoice: FinancialDocumentsValidations[]) {
        if (possiblySelectedChoice.length === 1) {
            return Object.assign(
                {
                    income_or_asset_type: possiblySelectedChoice[0]?.income_or_asset_type,
                    estimated_amount: '',
                    uploadedDocuments: {},
                },
                props.initialValues
            );
        }

        return Object.assign(
            {
                income_or_asset_type: '',
                estimated_amount: '',
                uploadedDocuments: {},
            },
            props.initialValues
        );
    }

    function onChangeSelect(
        e: React.ChangeEvent<{ name?: string; value: unknown }>,
        handleChange: (e: React.ChangeEvent<{ name?: string; value: unknown }>) => void,
        setFieldValue: import('formik').FormikHelpers<FinancialSourceFormFormikValues>['setFieldValue']
    ) {
        handleChange(e);
        // clear other if other field becomes hidden
        setFieldValue('other', null);
        setFieldValue('uploadedDocuments', {});

        // This makes sure there's no error when you change the initial select box value, otherwise state
        // will still be blank until next re-render.
        setFieldValue('income_or_asset_type', e?.target?.value);
    }

    const uploadedAllDocuments = (
        uploadedDocuments: FinancialSourceFormFormikValues['uploadedDocuments'],
        type: number
    ): boolean => {
        const config = props.config?.financial_documents_validations;
        const requirement = config?.find((doc) => doc.income_or_asset_type === type);

        if (!requirement) return true;

        const requireAll = requirement?.require_all ?? true;

        const metMinimumRequired = (doc: { id: number; min_required: number }): boolean => {
            const countUploaded = uploadedDocuments[String(doc.id)]
                ? uploadedDocuments[String(doc.id)]?.files.length
                : 0;

            if (countUploaded && !Number.isNaN(Number(countUploaded))) {
                return countUploaded >= doc.min_required;
            }

            return false;
        };

        if (!requireAll && props.financialType === FINANCIAL_STREAM_VOUCHER) {
            return true;
        }

        if (requireAll) {
            return Boolean(requirement?.proof_documents?.every(metMinimumRequired));
        }

        return Boolean(requirement.proof_documents?.some(metMinimumRequired));
    };

    const getSubmitButtonText = (): any => {
        if (props.isEditing) {
            return 'Save Changes';
        }
        if (isAsset || isVoucher) {
            return `Add ${financialTypeLabel}`;
        }
        if (isIncome) {
            return `Add ${financialTypeLabel} Source`;
        }
    };

    const getEstimatedAmountFieldLabel = (): any => {
        if (isAsset) {
            return 'Estimated asset balance';
        }
        if (isIncome) {
            return 'Estimated annual income';
        }
        if (isVoucher) {
            return 'Monthly voucher amount';
        }
    }

    const onSubmit: AddFinancialSourceFormProps['onSubmit'] = async (values, context) => {
        const documentsToUpload = [];
        const alreadyUploadedDocuments = [];
        bankingContext.toggleLoader(true);

        if (values) {
            for (const [proof_type_id, document] of Object.entries(values.uploadedDocuments)) {
                for (const { file, id } of document.files) {
                    // If there is a binary file, upload it, otherwise it means we have an already uploaded file
                    file
                        ? documentsToUpload.push({
                              content: await fileLikeObjectToBase64(file),
                              type: file.type,
                              name: file.name,
                              size: file.size,
                              proof_type_id: parseInt(proof_type_id),
                              context: {
                                  browser_name: browserName,
                                  browser_version: browserVersion,
                                  os_name: osName,
                                  os_version: osVersion,
                                  is_private_browsing: isPrivateBrowsing,
                                  is_desktop: isDesktop,
                              },
                          })
                        : alreadyUploadedDocuments.push(id);
                }
            }

            try {
                values.uploaded_documents = [
                    ...alreadyUploadedDocuments,
                    ...(documentsToUpload.length
                        ? (await new VeryGoodSecurityApi().uploadFiles(documentsToUpload)).map((d) => d.id)
                        : []),
                ];

                if (props?.onSubmit) {
                    // We want to create/patch the financial proof document objects only when the file upload has succeeded
                    props.onSubmit(values, context);
                }
            } catch (e) {
                logToSentry(e);

                if (props?.setError) {
                    props.setError([ERROR_UPLOAD]);
                }

                bankingContext.toggleLoader(false);
            }
        }
    };

    return (
        <Formik
            validationSchema={Yup.object({
                income_or_asset_type: Yup.number().required('Required'),
                estimated_amount: Yup.string().required('Required'),
                other: Yup.string().when('income_or_asset_type', {
                    is: (value: any) => [INCOME_TYPE_OTHER, ASSET_TYPE_OTHER].includes(value),
                    then: Yup.string().nullable().required('Required'),
                    otherwise: Yup.string().nullable().notRequired(),
                }),
            })}
            onSubmit={onSubmit}
            initialValues={getInitialValues(selectChoices)}
        >
            {({ values, handleChange, handleSubmit, errors, submitCount, isSubmitting, setFieldValue }) => {
                return (
                    <form onSubmit={handleSubmit}>
                        <div className="align-left">
                            <FormControl fullWidth>
                                <InputLabel htmlFor="income-or-asset-type">
                                    {financialTypeLabel} type
                                </InputLabel>
                                <Select
                                    disabled={Boolean(props.isEditing)}
                                    error={Boolean(values?.income_or_asset_type && errors.income_or_asset_type)}
                                    value={values.income_or_asset_type}
                                    fullWidth
                                    onChange={(e) => onChangeSelect(e, handleChange, setFieldValue)}
                                    inputProps={{
                                        name: 'income_or_asset_type',
                                        id: 'income-or-asset-type',
                                    }}
                                >
                                    {selectChoices.map((choice) => {
                                        const menuItemQuery = find({value: choice.income_or_asset_type})(
                                            ALL_INCOME_OR_ASSET_TYPES
                                        ) as { label: string; value: number };

                                        return (
                                            <MenuItem
                                                key={choice.income_or_asset_type}
                                                value={choice.income_or_asset_type}
                                            >
                                                {menuItemQuery?.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            {[INCOME_TYPE_OTHER, ASSET_TYPE_OTHER].includes(Number(values?.income_or_asset_type)) && (
                                <>
                                    <Spacer height={24}/>
                                    <FormTextInput
                                        label="Description"
                                        name="other"
                                        inputProps={{maxLength: 255}}
                                        value={values.other}
                                        handleChange={handleChange}
                                        error={errors.other}
                                        submitted={submitCount > 0}
                                    />
                                </>
                            )}
                            <Spacer height={24}/>
                            {values?.income_or_asset_type && (
                                <CurrencyTextField
                                    error={submitCount > 0 && !!errors.estimated_amount}
                                    helperText={submitCount > 0 && errors.estimated_amount}
                                    fullWidth
                                    textAlign="left"
                                    label={getEstimatedAmountFieldLabel()}
                                    minimumValue="0"
                                    name="estimated_amount"
                                    currencySymbol="$"
                                    onChange={(_event, value) => setFieldValue('estimated_amount', value)}
                                    outputFormat="string"
                                    value={values.estimated_amount}
                                    inputProps={{autoComplete: 'off'}}
                                    modifyValueOnWheel={false}
                                />
                            )}
                            {Number(values.income_or_asset_type) === INCOME_OR_ASSET_TYPE_VOUCHER_HOUSING_AUTHORITY && (
                                <>
                                    <Field
                                        fullWidth
                                        margin="normal"
                                        label="Case Manager Name (If Applicable)"
                                        name="case_manager_name"
                                        component={TextField}
                                        autoComplete="none"
                                    />
                                    <Field
                                        fullWidth
                                        margin="normal"
                                        label="Case Manager Contact Information (If Applicable) "
                                        name="case_manager_contact_info"
                                        component={TextField}
                                        autoComplete="none"
                                    />
                                </>
                            )}
                            {values.income_or_asset_type && (
                                <UploadDocuments
                                    removeFile={(docId, fileId) => {
                                        if (values.uploadedDocuments[docId]) {
                                            values.uploadedDocuments[docId].files = values.uploadedDocuments[
                                                docId
                                                ].files.filter((f) => f.id !== fileId);
                                        }

                                        if (values.uploadedDocuments[docId]?.files.length === 0) {
                                            delete values.uploadedDocuments[docId];
                                        }
                                        setFieldValue('uploadedDocuments', values.uploadedDocuments);
                                    }}
                                    removeAll={(docId) => {
                                        const uploadedDocuments = omit(values.uploadedDocuments, [docId]);
                                        setFieldValue('uploadedDocuments', uploadedDocuments);
                                    }}
                                    incomeOrAssetType={Number(values.income_or_asset_type)}
                                    streamType={props.financialType}
                                    uploadedDocuments={values.uploadedDocuments}
                                    loadDocument={(e) => setFieldValue('uploadedDocuments', e)}
                                    setError={(err) => {
                                        if (props.setError) {
                                            props.setError(err);
                                        }
                                    }}
                                />
                            )}
                        </div>
                        <ActionButton
                            disabled={
                                (!props.isEditing && !allValuesSet(values, {
                                    exclude: [
                                        'other',
                                        'case_manager_name',
                                        'case_manager_contact_info',
                                    ]
                                })) ||
                                isSubmitting ||
                                !uploadedAllDocuments(values.uploadedDocuments, Number(values.income_or_asset_type))
                            }
                            marginTop={68}
                            marginBottom={20}
                        >
                            {getSubmitButtonText()}
                        </ActionButton>
                    </form>
                );
            }}
        </Formik>
    );
}

const mapStateToProps = (
    state: Readonly<{
        configuration: ConfigurationState;
    }>
) => ({
    config: state.configuration,
});

export default connect(mapStateToProps)(AddFinancialSourceForm);
